import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import {
  useSurfacePreferences,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import type { BufferStyle } from "@/types/userPreferences.ts";
import { CalendarSelection } from "./Calendars.tsx";
import { MeetingPreferencesList } from "./MeetingPreferences.tsx";
import { ProtectedMeetings } from "./ProtectedMeetings.tsx";
import { panelDescriptionClass, SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

const BUFFER_STYLE_OPTIONS: Array<{ value: BufferStyle; label: string }> = [
  { value: "backToBack", label: "Back-to-back meetings" },
  { value: "buffersAfterEach", label: "Buffers after each meeting" },
  { value: "buffersAfterXHours", label: "Buffers after X continuous hours" },
];

export const SchedulingPreferences = () => {
  const prefs = useSurfacePreferences("scheduling");
  const calendarPrefs = useSurfacePreferences("calendars");
  const updatePreferences = useUpdateSurfacePreferences("scheduling");

  const save = async (delta: Record<string, unknown>) => {
    try {
      await updatePreferences(delta);
    } catch {
      errorToast("Failed to update scheduling preferences. Please try again.");
    }
  };

  return (
    <>
      {/* Header outside the space-y root so the gap under the title stays the
          standard mb-6 instead of the section rhythm. */}
      <SettingsPanelHeader title="Scheduling" />
      <p className={panelDescriptionClass}>
        Your calendars and preferences for how meetings get scheduled.
      </p>
      <div className="w-full space-y-8 pb-12">
        <div>
          <h2 className="text-base font-semibold mb-2">Calendars</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Select the calendars that determine your availability
          </p>
          <CalendarSelection selectedCalendarIds={calendarPrefs.selectedCalendarIds} />
        </div>

        <div>
          <h2 className="text-base font-semibold mb-2">Scheduling Habits</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Configure your general scheduling preferences like buffer times and meeting clustering
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bufferStyle">Buffer Style</Label>
              <Select
                value={prefs.bufferStyle}
                onValueChange={(v) => save({ bufferStyle: v as BufferStyle })}
              >
                <SelectTrigger id="bufferStyle" className="bg-white rounded-lg border-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUFFER_STYLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                "Back-to-back meetings" adds no buffer time between meetings.
              </p>
            </div>
            {prefs.bufferStyle === "buffersAfterXHours" && (
              <div className="space-y-2">
                <Label htmlFor="bufferHours">Hours before buffer</Label>
                <Select
                  value={String(prefs.bufferHours)}
                  onValueChange={(v) => save({ bufferHours: Number(v) })}
                >
                  <SelectTrigger id="bufferHours" className="bg-white rounded-lg border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((hours) => (
                      <SelectItem key={hours} value={String(hours)}>
                        {hours} {hours === 1 ? "hour" : "hours"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox
                id="travelBuffer"
                checked={prefs.travelBuffer}
                onCheckedChange={(checked) => save({ travelBuffer: checked === true })}
              />
              <Label htmlFor="travelBuffer">Travel buffer for in-person meetings</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="oneOnOnePacking"
                checked={prefs.oneOnOnePacking}
                onCheckedChange={(checked) => save({ oneOnOnePacking: checked === true })}
              />
              <Label htmlFor="oneOnOnePacking">Cluster 1:1s together</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="ignoreAutoGeneratedEventConflicts"
                checked={prefs.ignoreAutoGeneratedEventConflicts}
                onCheckedChange={(checked) =>
                  save({ ignoreAutoGeneratedEventConflicts: checked === true })
                }
              />
              <Label htmlFor="ignoreAutoGeneratedEventConflicts">
                Ignore conflicts with{" "}
                {/* Open the help doc without the surrounding label toggling the checkbox. */}
                <a
                  href="https://support.google.com/calendar/answer/6084018"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  auto-generated Google events
                </a>
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="createVirtualLinksForInPersonEvents"
                checked={prefs.createVirtualLinksForInPersonEvents}
                onCheckedChange={(checked) =>
                  save({ createVirtualLinksForInPersonEvents: checked === true })
                }
              />
              <Label htmlFor="createVirtualLinksForInPersonEvents">
                Create virtual meeting links for in-person events
              </Label>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-base font-semibold mb-2">Meeting Preferences</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Teach the scheduler how to handle different meeting types: typical duration,
            availability windows, virtual or in-person, and naming convention.
          </p>
          <MeetingPreferencesList />
        </div>

        <div>
          <h2 className="text-base font-semibold mb-2">Protected Meetings</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Protected recurring meetings will never be offered as available time by the scheduler.
            Unprotected meetings are treated as flexible and may be used as fallback.
          </p>
          <ProtectedMeetings />
        </div>
      </div>
    </>
  );
};
